import * as React from "react";
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";

import { useIsMobile } from "@/hooks/use-mobile";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

const chartConfig = {
  activity: { label: "Activity" },
  logins: { label: "User Logins", color: "var(--chart-1)" },
  care_shifts: { label: "Care Shifts", color: "var(--chart-2)" },
};

export function ChartAreaInteractive({
  series = [],
  days = 7,
  onDaysChange,
  title = "Platform Activity",
  description = "Logins and care shifts over time",
}) {
  const isMobile = useIsMobile();
  const timeRange = days === 30 ? "30d" : "7d";

  React.useEffect(() => {
    if (isMobile && days !== 7) {
      onDaysChange?.(7);
    }
  }, [isMobile, days, onDaysChange]);

  const setTimeRange = (value) => {
    if (!value) return;
    onDaysChange?.(value === "30d" ? 30 : 7);
  };

  const chartData = series.map((point) => ({
    date: point.date,
    logins: point.logins || 0,
    care_shifts: point.care_shifts || 0,
  }));

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          <span className="hidden @[540px]/card:block">{description}</span>
          <span className="@[540px]/card:hidden">
            {days === 30 ? "Last 30 days" : "Last 7 days"}
          </span>
        </CardDescription>
        <CardAction>
          <ToggleGroup
            type="single"
            value={timeRange}
            onValueChange={setTimeRange}
            variant="outline"
            className="hidden *:data-[slot=toggle-group-item]:!px-4 @[767px]/card:flex"
          >
            <ToggleGroupItem value="30d">Last 30 days</ToggleGroupItem>
            <ToggleGroupItem value="7d">Last 7 days</ToggleGroupItem>
          </ToggleGroup>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger
              className="flex w-40 @[767px]/card:hidden"
              size="sm"
              aria-label="Select a value"
            >
              <SelectValue placeholder="Last 7 days" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="30d" className="rounded-lg">
                Last 30 days
              </SelectItem>
              <SelectItem value="7d" className="rounded-lg">
                Last 7 days
              </SelectItem>
            </SelectContent>
          </Select>
        </CardAction>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="fillLogins" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-logins)" stopOpacity={1.0} />
                <stop offset="95%" stopColor="var(--color-logins)" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="fillShifts" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-care_shifts)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-care_shifts)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                const date = new Date(`${value}T00:00:00`);
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                });
              }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) =>
                    new Date(`${value}T00:00:00`).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })
                  }
                  indicator="dot"
                />
              }
            />
            <Area
              dataKey="care_shifts"
              type="natural"
              fill="url(#fillShifts)"
              stroke="var(--color-care_shifts)"
              stackId="a"
            />
            <Area
              dataKey="logins"
              type="natural"
              fill="url(#fillLogins)"
              stroke="var(--color-logins)"
              stackId="a"
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
